// ─── FOTOS DE ARTÍCULOS DEL INVENTARIO ───────────────────────────────────────
// Galería por tela: subir varias fotos (opcionalmente por color), verlas en
// grande y borrarlas. Las fotos se comprimen en el navegador antes de subirse
// (JPEG de máx. 1600 px + miniatura de 320 px) para que pesen poco.
import { useState } from "react";
import { api, urlApi } from "../auth";

export const urlFoto = (id) => urlApi(`/inventario/fotos/archivo/${id}`);
// Clave con la que se ligan las fotos: la clave de Estrategia (sobrevive a recargas).
export const claveFoto = (it) => String(it?.id || it?.descripcion || "").trim();

// Redimensiona y convierte a JPEG con canvas.
async function comprimir(archivo, maximo, calidad) {
  const url = URL.createObjectURL(archivo);
  try {
    const img = await new Promise((ok, mal) => {
      const i = new Image();
      i.onload = () => ok(i);
      i.onerror = () => mal(new Error("No se pudo leer la imagen"));
      i.src = url;
    });
    const escala = Math.min(1, maximo / Math.max(img.width, img.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(img.width * escala);
    canvas.height = Math.round(img.height * escala);
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise((ok) => canvas.toBlob(ok, "image/jpeg", calidad));
    if (!blob) throw new Error("No se pudo comprimir la imagen");
    return new File([blob], (archivo.name || "foto").replace(/\.[^.]+$/, "") + ".jpg", { type: "image/jpeg" });
  } finally {
    URL.revokeObjectURL(url);
  }
}

export async function subirFoto(clave, archivo, color = "") {
  const grande = await comprimir(archivo, 1600, 0.85);
  const mini = await comprimir(archivo, 320, 0.75);
  return api.upload("/inventario/fotos", grande, { clave, color, miniatura: mini });
}

export default function FotosArticulo({ item, fotos, colores = [], onCambio, onCerrar }) {
  const clave = claveFoto(item);
  const [sel, setSel] = useState(0);
  const [color, setColor] = useState("");
  const [subiendo, setSubiendo] = useState(false);
  const [error, setError] = useState("");
  const actual = fotos[Math.min(sel, fotos.length - 1)];

  const subir = async (archivos) => {
    const lista = Array.from(archivos || []);
    if (!lista.length) return;
    setError("");
    setSubiendo(true);
    const nuevas = [];
    for (const a of lista) {
      try {
        nuevas.push(await subirFoto(clave, a, color));
      } catch (e) {
        setError(`${a.name}: ${/"detail":"([^"]+)"/.exec(String(e.message))?.[1] || e.message || "no se pudo subir"}`);
      }
    }
    setSubiendo(false);
    if (nuevas.length) {
      onCambio([...fotos, ...nuevas]);
      setSel(fotos.length + nuevas.length - 1);
    }
  };

  const borrar = async () => {
    if (!actual || !window.confirm("¿Borrar esta foto?")) return;
    try {
      await api.del(`/inventario/fotos/${actual.id}`);
      onCambio(fotos.filter((f) => f.id !== actual.id));
      setSel((s) => Math.max(0, s - 1));
    } catch (e) {
      setError("No se pudo borrar la foto.");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 flex items-start sm:items-center justify-center p-3 overflow-y-auto"
      onClick={() => !subiendo && onCerrar()}
      data-testid="fotos-modal"
    >
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-3xl p-4 space-y-3 my-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-xs font-semibold text-teal-700 uppercase tracking-wider">Fotos</p>
            <p className="text-sm text-slate-800 font-semibold">
              {item.descripcion || "—"} <span className="font-mono font-normal text-slate-500">{item.id}</span>
            </p>
          </div>
          <button onClick={onCerrar} disabled={subiendo} className="text-slate-400 hover:text-slate-600 text-2xl leading-none px-2" title="Cerrar">
            ×
          </button>
        </div>

        {actual ? (
          <div className="relative bg-slate-100 rounded-xl flex items-center justify-center min-h-[240px]">
            <img src={urlFoto(actual.id)} alt={item.descripcion} className="max-h-[60vh] max-w-full object-contain rounded-xl" data-testid="fotos-grande" />
            {actual.color && (
              <span className="absolute top-2 left-2 text-[11px] font-semibold bg-white/90 text-slate-700 px-2 py-0.5 rounded">{actual.color}</span>
            )}
            {fotos.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={() => setSel((s) => (s - 1 + fotos.length) % fotos.length)}
                  className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/90 rounded-full w-8 h-8 text-lg"
                >
                  ‹
                </button>
                <button
                  type="button"
                  onClick={() => setSel((s) => (s + 1) % fotos.length)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/90 rounded-full w-8 h-8 text-lg"
                >
                  ›
                </button>
              </>
            )}
          </div>
        ) : (
          <div className="bg-slate-50 border border-dashed border-slate-300 rounded-xl py-12 text-center text-sm text-slate-400">
            Esta tela todavía no tiene fotos.
          </div>
        )}

        {fotos.length > 1 && (
          <div className="flex gap-2 overflow-x-auto pb-1">
            {fotos.map((f, i) => (
              <button
                key={f.id}
                type="button"
                onClick={() => setSel(i)}
                className={`shrink-0 rounded-lg overflow-hidden border-2 ${i === sel ? "border-teal-500" : "border-transparent"}`}
                title={f.color || ""}
              >
                <img src={urlFoto(f.mini_id || f.id)} alt="" className="w-16 h-16 object-cover" />
              </button>
            ))}
          </div>
        )}

        {error && <p className="text-xs text-rose-600 font-medium">{error}</p>}

        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            {colores.length > 0 && (
              <select
                value={color}
                onChange={(e) => setColor(e.target.value)}
                className="border border-slate-300 rounded-lg px-2 py-1.5 text-xs"
                title="Color de la foto (opcional)"
                data-testid="fotos-color"
              >
                <option value="">Foto general (sin color)</option>
                {colores.map((c) => (
                  <option key={c} value={c}>
                    Color: {c}
                  </option>
                ))}
              </select>
            )}
            <label
              className={`text-xs font-semibold px-3 py-2 rounded-lg cursor-pointer whitespace-nowrap ${
                subiendo ? "opacity-50 pointer-events-none bg-teal-600 text-white" : "bg-teal-600 text-white hover:bg-teal-700"
              }`}
            >
              {subiendo ? "Subiendo…" : "📷 Agregar fotos"}
              <input
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                data-testid="fotos-subir"
                onChange={(e) => {
                  subir(e.target.files);
                  e.target.value = "";
                }}
              />
            </label>
          </div>
          {actual && (
            <button type="button" onClick={borrar} className="text-xs font-semibold px-3 py-2 rounded-lg text-rose-600 hover:bg-rose-50" data-testid="fotos-borrar">
              Borrar esta foto
            </button>
          )}
        </div>
        <p className="text-[11px] text-slate-400">
          Desde el celular puedes tomar la foto directo con la cámara. Las fotos se ligan a la clave {clave ? `«${clave}»` : ""}, así que se
          conservan aunque se vuelva a cargar el inventario.
        </p>
      </div>
    </div>
  );
}
